
import { AuthorizedUser } from '@/types/authorizedUser';

// Mock authorized users list - in a real app, this would come from a database
let authorizedUsers: AuthorizedUser[] = [
  { id: "1", name: "John Doe", email: "john@example.com", accountNumber: "ACC123456", licenseNumber: "DL789012" },
  { id: "2", name: "Jane Smith", email: "jane@example.com", accountNumber: "ACC654321", licenseNumber: "DL210987" },
  { id: "3", name: "Driver User", email: "user@example.com", accountNumber: "ACC111222", licenseNumber: "DL333444" }
];

export const getAuthorizedUsers = (): Promise<AuthorizedUser[]> => {
  return new Promise((resolve) => {
    // Simulate API call delay
    setTimeout(() => {
      resolve([...authorizedUsers]);
    }, 600);
  });
};

export const addAuthorizedUser = (userData: Omit<AuthorizedUser, 'id'>): Promise<AuthorizedUser> => {
  return new Promise((resolve, reject) => {
    // Simulate API call delay
    setTimeout(() => {
      // Check if email is already used
      if (authorizedUsers.some(user => user.email === userData.email)) {
        reject(new Error('A user with this email already exists'));
        return;
      }
      
      // Check if account number is already used
      if (authorizedUsers.some(user => user.accountNumber === userData.accountNumber)) {
        reject(new Error('A user with this account number already exists'));
        return;
      }
      
      // Check if license number is already used
      if (authorizedUsers.some(user => user.licenseNumber === userData.licenseNumber)) {
        reject(new Error('A user with this license number already exists'));
        return;
      }
      
      // Create new user with unique ID
      const newUser: AuthorizedUser = {
        id: Date.now().toString(),
        ...userData
      };
      
      // Add to array
      authorizedUsers.push(newUser);
      
      resolve(newUser);
    }, 800);
  });
};

export const deleteAuthorizedUser = (userId: string): Promise<void> => {
  return new Promise((resolve) => {
    // Simulate API call delay
    setTimeout(() => {
      authorizedUsers = authorizedUsers.filter(user => user.id !== userId);
      resolve();
    }, 600);
  });
};

export const verifyDriverCredentials = (accountNumber: string, licenseNumber: string): Promise<boolean> => {
  return new Promise((resolve) => {
    // Simulate API call delay
    setTimeout(() => {
      // Check if the provided credentials match any authorized user
      const isAuthorized = authorizedUsers.some(user => 
        user.accountNumber === accountNumber && user.licenseNumber === licenseNumber
      );
      
      resolve(isAuthorized);
    }, 500);
  });
};

export const bulkImportUsers = (users: Omit<AuthorizedUser, 'id'>[]): Promise<AuthorizedUser[]> => {
  return new Promise((resolve, reject) => {
    // Simulate API call delay
    setTimeout(() => {
      const importedUsers: AuthorizedUser[] = [];
      const errors: string[] = [];
      
      // Process each user
      users.forEach((userData, index) => {
        // Check for duplicates
        if (authorizedUsers.some(user => user.email === userData.email)) {
          errors.push(`User #${index + 1}: Email ${userData.email} already exists`);
          return;
        }
        
        if (authorizedUsers.some(user => user.accountNumber === userData.accountNumber)) {
          errors.push(`User #${index + 1}: Account number ${userData.accountNumber} already exists`);
          return;
        }
        
        if (authorizedUsers.some(user => user.licenseNumber === userData.licenseNumber)) {
          errors.push(`User #${index + 1}: License number ${userData.licenseNumber} already exists`);
          return;
        }
        
        // Create new user with unique ID
        const newUser: AuthorizedUser = {
          id: `imp-${Date.now()}-${index}`,
          ...userData
        };
        
        // Add to array
        authorizedUsers.push(newUser);
        importedUsers.push(newUser);
      });
      
      if (errors.length > 0) {
        // If there are errors but also some successful imports
        if (importedUsers.length > 0) {
          resolve(importedUsers); // Return the successful imports
        } else {
          reject(new Error(`Import failed: ${errors.join(', ')}`));
        }
      } else {
        resolve(importedUsers);
      }
    }, 1000);
  });
};
